'use client'

import { useId } from 'react'
import { cn } from '@/lib/utils'

/**
 * SOLO — the flight school AI's helmet, and the ONLY home of the character.
 *
 * Replaces the Biggles line-art mark (Wayne, 2026-09-01; geometry is his
 * SoloAvatar component, unchanged). Two deliberate differences from the mark
 * it retires:
 *
 * - It INVERTS with the mode, on purpose. Biggles sat on a fixed pale disc
 *   because thin line art blooms into noise on a dark ground; this helmet is
 *   thick, simple geometry, so the light/dark remap works — and the dark
 *   neon treatment is the stronger of the two (verified in the 2026-09-01
 *   render review, legible down to 24px vs the old mark's 40px floor).
 *   Colours live in globals.css as the `--solo-*` tokens, defined in :root
 *   and .dark only and inherited by every palette: the mascot must not
 *   change clothes when the palette does.
 *
 * - The glow filter id comes from useId(). The mark renders in two places at
 *   once (mobile-header launcher + panel header), and duplicate SVG ids make
 *   every instance resolve to the first one's filter. React's generated id
 *   is sanitized because its delimiter characters are not safe inside a
 *   `url(#...)` fragment reference.
 */
/**
 * The light-mode literals, repeated here as var() FALLBACKS.
 *
 * NOT belt-and-braces — this is the fix for a real outage (2026-09-01, Wayne:
 * "SOLO is not showing up as an icon on mobile or desktop"). A bare
 * `var(--solo-stroke)` whose token is missing is an INVALID value, so the
 * declaration is dropped and `stroke`/`fill` fall back to SVG's initial
 * `none` — a correctly-sized 44px box painting absolutely nothing, with no
 * error in any console. Reproduced exactly: strip the tokens and the computed
 * style reads `fill: none; stroke: none` at 44x44, which is the blank square
 * in Wayne's screenshot.
 *
 * The trigger was a browser holding CSS from before the tokens shipped while
 * running the new JS, but the token can also go missing from a rename, a
 * palette that forgets to inherit them, or a partial deploy — and every one
 * of those failures is silent. Same rule the maintenance shadow already
 * follows: an unparseable colour must fall back rather than reach CSS,
 * because the browser drops the whole declaration and the thing vanishes.
 * The retired Biggles mark could not fail this way; it painted from
 * constants.
 *
 * The LIGHT values are the right fallback because they are self-contained —
 * an opaque near-white shell with dark ink reads on a dark ground as well as
 * a light one, so a degraded mark is legible either way.
 */
const FALLBACK = {
  stroke: '#334155',
  shell: '#f1f5f9',
  ear: '#e2e8f0',
  goggle: '#334155',
  visor: '#0f172a',
  eyes: '#22d3ee',
  mouth: '#334155',
  notch: '#fbbf24',
  'notch-stroke': '#334155',
} as const

/** `var(--solo-x, <light literal>)`. Never write a bare var() in this file. */
function ink(part: keyof typeof FALLBACK): string {
  return `var(--solo-${part}, ${FALLBACK[part]})`
}

export function SoloMark({
  className,
  title,
}: {
  className?: string
  /** Accessible name. Omit for decorative use (a visible "SOLO" nearby). */
  title?: string
}) {
  const glowId = 'solo-glow-' + useId().replace(/[^a-zA-Z0-9_-]/g, '')

  return (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={cn('block shrink-0', className)}
    >
      <defs>
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Helmet shell */}
      <path
        d="M 22 52 C 20 28, 32 14, 50 14 C 68 14, 80 28, 78 52 C 77 66, 70 76, 50 76 C 30 76, 23 66, 22 52 Z"
        style={{ stroke: ink('stroke'), fill: ink('shell') }}
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Ear guards */}
      <path
        d="M 18 46 C 18 40, 24 40, 24 48 C 24 58, 19 62, 18 56 Z"
        style={{ stroke: ink('stroke'), fill: ink('ear') }}
        strokeWidth={3}
      />
      <path
        d="M 82 46 C 82 40, 76 40, 76 48 C 76 58, 81 62, 82 56 Z"
        style={{ stroke: ink('stroke'), fill: ink('ear') }}
        strokeWidth={3}
      />

      {/* Goggle mount notch */}
      <path
        d="M 38 24 L 44 21 L 56 21 L 62 24 Z"
        style={{ stroke: ink('notch-stroke'), fill: ink('notch') }}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />

      {/* Goggle frame + visor */}
      <rect
        x={24}
        y={32}
        width={52}
        height={26}
        rx={13}
        style={{ stroke: ink('goggle'), fill: ink('visor') }}
        strokeWidth={3.5}
      />

      {/* Digital eyes */}
      <path
        d="M 35 45 C 35 40, 43 40, 43 45"
        style={{ stroke: ink('eyes') }}
        strokeWidth={3.5}
        strokeLinecap="round"
        filter={`url(#${glowId})`}
      />
      <path
        d="M 57 45 C 57 40, 65 40, 65 45"
        style={{ stroke: ink('eyes') }}
        strokeWidth={3.5}
        strokeLinecap="round"
        filter={`url(#${glowId})`}
      />

      {/* Smile */}
      <path
        d="M 44 65 Q 50 69 56 65"
        style={{ stroke: ink('mouth') }}
        strokeWidth={3}
        strokeLinecap="round"
      />
    </svg>
  )
}
